import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import PublicHeader from '../components/PublicHeader';
import { Check, FileText, Users, BarChart3, CreditCard, ArrowRight, Megaphone, X } from 'lucide-react';
import { Seo, SITE_URL, PLAN_PRICING, faqJsonLd } from '../components/Seo';

const TEMPLATES = [
  { key: 'brutalist', label: 'Brutalist' },
  { key: 'classic', label: 'Classic' },
  { key: 'modern', label: 'Modern' },
  { key: 'minimal', label: 'Minimal' },
  { key: 'aesthetic', label: 'Aesthetic' },
];

// Reused verbatim from About.js's ABOUT_FAQS (Stripe requirement, cancellation, data security, support).
const LANDING_FAQS = [
  {
    q: 'Do I need a Stripe account?',
    a: "Yes, to accept payments you'll need to connect a Stripe account. Don't worry\u2014it's free to create and takes just a few minutes. We'll guide you through the process in your Settings page.",
  },
  {
    q: 'Can I cancel anytime?',
    a: "Yes, you can cancel or change your plan at any time. If you downgrade, you'll keep your current plan's features until the end of your billing period.",
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. All data is encrypted in transit and at rest. Payment processing is handled entirely by Stripe\u2014we never see or store credit card information.',
  },
  {
    q: 'What kind of support do you offer?',
    a: 'Standard and Pro subscribers get instant AI support chat right inside the app, any time \u2014 it knows the product inside and out. Everyone, on any plan, can email support@createcollect.com and a human will get back to you, usually within one business day.',
  },
];

const LANDING_TITLE = 'CreateCollect — Invoicing & Client Marketing for Creatives';
const LANDING_DESC = 'Send beautiful invoices, get paid by card, and win repeat business with built-in email marketing. Made for photographers, designers, musicians, and filmmakers.';
const priceFor = (name) => PLAN_PRICING.find((p) => p.name === name).price;

const SOFTWARE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CreateCollect',
  url: SITE_URL,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description: LANDING_DESC,
  offers: PLAN_PRICING.map((p) => ({
    '@type': 'Offer',
    name: `${p.name} plan`,
    price: p.price.toFixed(2),
    priceCurrency: 'USD',
  })),
};

const ANNUAL_PRICING = {
  Standard: 55,
  Pro: 80,
};

const annualSavingsPercent = (name) => {
  const monthly = priceFor(name);
  const annual = ANNUAL_PRICING[name];
  if (!monthly || !annual) return null;
  return Math.round((1 - annual / (monthly * 12)) * 100);
};

const Landing = () => {
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');
  const [billingInterval, setBillingInterval] = useState(() =>
    new URLSearchParams(window.location.search).get('billing') === 'annual' ? 'year' : 'month'
  );

  useEffect(() => {
    if (window.location.hash === '#pricing') {
      const el = document.getElementById('pricing');
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, []);

  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxSrc]);

  const openLightbox = (key, label) => {
    setLightboxSrc(`/templates/${key}.png`);
    setLightboxAlt(`${label} invoice template example`);
  };

  const plans = [
    {
      name: 'Free',
      price: `$${priceFor('Free')}`,
      period: '/month',
      description: 'Perfect for getting started',
      features: ['2 invoices per month', 'Client management', 'Email invoices & PDF downloads', 'Online card payments (Stripe)'],
      cta: 'Get Started',
      registerHref: '/register',
      popular: false
    },
    {
      name: 'Standard',
      price: billingInterval === 'year' ? `$${ANNUAL_PRICING.Standard}` : `$${priceFor('Standard')}`,
      period: billingInterval === 'year' ? '/year' : '/month',
      savings: billingInterval === 'year' ? annualSavingsPercent('Standard') : null,
      description: 'For growing creatives',
      features: ['5 invoices per month', 'Email marketing — 500 emails/month', 'All 5 invoice templates', 'Business reports & analytics', 'Remove CreateCollect branding', 'Instant AI support chat'],
      cta: 'Get Standard',
      registerHref: `/register?plan=standard&billing=${billingInterval}`,
      popular: true
    },
    {
      name: 'Pro',
      price: billingInterval === 'year' ? `$${ANNUAL_PRICING.Pro}` : `$${priceFor('Pro')}`,
      period: billingInterval === 'year' ? '/year' : '/month',
      savings: billingInterval === 'year' ? annualSavingsPercent('Pro') : null,
      description: 'Unlimited creative power',
      features: ['Unlimited invoices', 'Email marketing — 2,000 emails/month', 'Recurring & scheduled invoices', 'Automatic payment reminders', 'Advanced analytics & priority support', 'Instant AI support chat'],
      cta: 'Go Pro',
      registerHref: `/register?plan=pro&billing=${billingInterval}`,
      popular: false
    }
  ];

  // Features — palette allocation:
  //   Index 4 (Repeat Business) → full Riso Blue panel
  //   Index 2 (Analytics)       → Flash Yellow panel
  //   Others                     → paper with ink border + small orange tag
  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      tag: '01 / Invoices',
      title: 'Beautiful Invoices',
      description: 'Create stunning invoices that reflect your creative brand in seconds.'
    },
    {
      icon: <Users className="w-6 h-6" />,
      tag: '02 / Clients',
      title: 'Client Management',
      description: 'Keep all your clients organized and invoice them with a single click.'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      tag: '03 / Analytics',
      title: 'Insightful Analytics',
      description: 'Track your earnings, pending payments, and business growth at a glance.'
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      tag: '04 / Payments',
      title: 'Get Paid Faster',
      description: 'Accept card payments directly through your invoices with Stripe.'
    },
    {
      icon: <Megaphone className="w-6 h-6" />,
      tag: '05 / Marketing',
      title: 'Repeat Business, Built In',
      description: 'Send email campaigns to past clients — announce new services, fill slow seasons, and track every open and click. No separate marketing tool needed.'
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <Seo title={LANDING_TITLE} description={LANDING_DESC} path="/" jsonLd={[SOFTWARE_JSON_LD, faqJsonLd(LANDING_FAQS)]} />

      {/* Header */}
      <PublicHeader secondaryLabel="About" secondaryHref="/about" />

      {/* Hero */}
      <section className="pt-36 pb-24 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-accent mb-6 block">
                ● &nbsp; Issue 02 &nbsp;/&nbsp; For Creatives
              </span>
              <h1 className="font-heading font-bold text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-tight mb-8">
                Get paid.
                <br />
                Stay <span className="italic text-accent">creative.</span>
              </h1>
              <p className="font-body text-lg md:text-xl text-foreground/75 mb-10 max-w-xl leading-relaxed">
                Invoices, card payments and repeat-client email marketing — in one tool built for photographers, designers, musicians and filmmakers.
              </p>
              <div className="flex flex-wrap gap-4 items-center">
                <Link to="/register">
                  <Button size="lg" className="gap-2 rounded-full px-8 h-12 text-base" data-testid="hero-cta">
                    Start for free <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button variant="outline" size="lg" className="rounded-full px-8 h-12 text-base border-foreground/30" data-testid="view-pricing-btn">
                    View Pricing
                  </Button>
                </a>
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-foreground/50 mt-5" data-testid="hero-no-card-note">
                No credit card required — 2 free invoices every month
              </p>
            </div>

            {/* Hero illustration — the illustration IS the full visual, no leftover card/stamp decoration */}
            <div className="lg:col-span-6" data-testid="hero-product-block">
              <img
                src="https://res.cloudinary.com/nwvsq3gc/image/upload/e_trim/w_1600,q_auto,f_auto/v1788391512/CC_final_scene1_2.png"
                alt="Hand-illustrated scene of a musician working alongside an anthropomorphized invoice and pencil character, representing CreateCollect's creative invoicing"
                className="hidden md:block w-full h-auto rounded-2xl"
                loading="eager"
                data-testid="hero-illustration-desktop"
              />
              <img
                src="https://res.cloudinary.com/nwvsq3gc/image/upload/e_trim/w_600,q_auto,f_auto/v1788391502/CC_pencil.png"
                alt="Hand-illustrated pencil character representing CreateCollect's creative invoicing"
                className="md:hidden w-64 h-64 sm:w-72 sm:h-72 mx-auto"
                loading="eager"
                data-testid="hero-illustration-mobile"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by creatives — paper strip */}
      <section className="py-14 px-6 border-y border-foreground/15 bg-background">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-foreground/60 mb-6">
            Trusted by creatives who&apos;d rather be making things
          </p>
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3">
            <span className="font-heading text-xl md:text-2xl">Photographers</span>
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            <span className="font-heading text-xl md:text-2xl">Designers</span>
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            <span className="font-heading text-xl md:text-2xl">Musicians</span>
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            <span className="font-heading text-xl md:text-2xl">Animators</span>
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            <span className="font-heading text-xl md:text-2xl">Filmmakers</span>
          </div>
          <p className="font-body text-sm text-foreground/60 mt-6 italic">
            Built by a working music producer and studio owner — for people who&apos;d rather be making things.
          </p>
        </div>
      </section>

      {/* Features — expressive grid, mixed palette */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-accent mb-4 block">
                § &nbsp; Features
              </span>
              <h2 className="font-heading font-bold text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-2xl">
                Everything you need.
                <br />
                Nothing you don&apos;t.
              </h2>
            </div>
            <p className="font-body text-base text-foreground/70 max-w-sm md:text-right">
              Five tools, one workflow — from the first invoice to the follow-up email that fills next month&apos;s calendar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const isBlue = index === 4; // Repeat Business, full Riso Blue panel
              const isYellow = index === 2; // Analytics, Flash Yellow panel

              if (isBlue) {
                return (
                  <div
                    key={index}
                    data-testid={`feature-card-${index}`}
                    className="md:col-span-2 lg:col-span-2 bg-primary text-primary-foreground rounded-2xl p-10 flex flex-col justify-between min-h-[280px] border border-foreground/10"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[11px] uppercase tracking-[0.28em] opacity-80">
                        {feature.tag}
                      </span>
                      <div className="w-12 h-12 rounded-full bg-primary-foreground/10 flex items-center justify-center">
                        {feature.icon}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-3 leading-tight">
                        {feature.title}
                      </h3>
                      <p className="font-body text-base md:text-lg leading-relaxed max-w-xl opacity-90">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              }

              if (isYellow) {
                return (
                  <div
                    key={index}
                    data-testid={`feature-card-${index}`}
                    className="bg-accent text-accent-foreground rounded-2xl p-8 flex flex-col justify-between min-h-[280px] border border-foreground/20"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[11px] uppercase tracking-[0.28em] opacity-80">
                        {feature.tag}
                      </span>
                      <div className="w-12 h-12 rounded-full bg-accent-foreground/10 flex items-center justify-center">
                        {feature.icon}
                      </div>
                    </div>
                    <div className="mt-8">
                      <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight mb-3 leading-tight">
                        {feature.title}
                      </h3>
                      <p className="font-body text-base leading-relaxed opacity-90">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={index}
                  data-testid={`feature-card-${index}`}
                  className="bg-card text-card-foreground rounded-2xl p-8 border border-foreground/15 hover:border-foreground/40 transition-colors flex flex-col justify-between min-h-[280px]"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-block bg-accent text-accent-foreground font-mono text-[10px] uppercase tracking-[0.24em] px-2.5 py-1 rounded-full">
                      {feature.tag}
                    </span>
                    <div className="text-accent">
                      {feature.icon}
                    </div>
                  </div>
                  <div className="mt-8">
                    <h3 className="font-heading font-bold text-2xl tracking-tight mb-3 leading-tight">
                      {feature.title}
                    </h3>
                    <p className="font-body text-base text-foreground/70 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Focus on your craft — paper dominant with ink marker + blue underline flourish */}
      <section className="py-24 px-6 border-y border-foreground/15">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://res.cloudinary.com/nwvsq3gc/image/upload/w_3000/e_trim/w_1400,q_auto,f_auto/v1788391511/CC_final_scene2.png"
                alt="Hand-illustrated scene of a laptop, coffee mug and newspaper, representing a creative's desk on CreateCollect"
                className="w-full h-auto"
                loading="lazy"
                data-testid="craft-section-illustration"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-accent mb-4 block">
                ✱ &nbsp; Made for you
              </span>
              <h2 className="font-heading font-bold text-4xl md:text-6xl leading-[0.95] tracking-tight mb-6">
                Focus on your craft,
                <br />
                <span className="relative inline-block">
                  not paperwork
                  <span aria-hidden="true" className="absolute left-0 -bottom-2 h-2 w-full bg-primary" />
                </span>
              </h2>
              <p className="font-body text-lg text-foreground/70 leading-relaxed mb-8 max-w-lg">
                Whether you&apos;re editing photos, mixing tracks, or animating scenes — your time is valuable. CreateCollect handles the boring stuff so you can focus on what you love doing.
              </p>
              <ul className="space-y-3">
                {[
                  'Create invoices in under 60 seconds',
                  'Automatic payment reminders (Pro)',
                  'Beautiful PDF exports',
                  'Email marketing to past clients — built in',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <span className="font-body text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Template Showcase */}
      <section className="py-24 px-6 relative overflow-hidden bg-background" data-testid="template-showcase">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-accent mb-4 block">
              F I V E &nbsp; T E M P L A T E S
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tight text-foreground mb-4 leading-[0.95]">
              FIVE TEMPLATES.
              <br />
              ONE THAT&apos;S YOURS.
            </h2>
            <p className="font-body text-foreground/70 max-w-xl mx-auto leading-relaxed">
              Your logo, your colors, your typography — on every invoice you send. Switch looks anytime.
            </p>
          </div>

          {/* Desktop / tablet fanned stack */}
          <div className="hidden md:block relative mx-auto" style={{ height: '640px', maxWidth: '900px' }}>
            {(() => {
              const fanConfig = [
                { key: 'aesthetic', label: 'Aesthetic', rot: -6, tx: -280, dim: true, mdHidden: true },
                { key: 'classic',   label: 'Classic',   rot: -3, tx: -140, dim: true, mdHidden: false },
                { key: 'modern',    label: 'Modern',    rot:  3,  tx:  140, dim: true, mdHidden: false },
                { key: 'minimal',   label: 'Minimal',   rot:  6,  tx:  280, dim: true, mdHidden: true },
                { key: 'brutalist', label: 'Brutalist', rot:  0,  tx:  0,   dim: false, mdHidden: false },
              ];
              return fanConfig.map(({ key, label, rot, tx, dim, mdHidden }, i) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => openLightbox(key, label)}
                  aria-label={`Preview ${label} invoice template`}
                  data-testid={`template-fan-${key}`}
                  className={`group absolute top-1/2 left-1/2 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded-md transition-all duration-300 ease-out ${mdHidden ? 'hidden lg:block' : ''}`}
                  style={{
                    transform: `translate(-50%, -50%) translateX(${tx}px) rotate(${rot}deg)`,
                    zIndex: i + 1,
                    opacity: dim ? 0.88 : 1,
                  }}
                >
                  <img
                    src={`/templates/${key}.png`}
                    alt={`${label} invoice template example`}
                    loading="lazy"
                    width="900"
                    height="1166"
                    className="block rounded-md border border-foreground/15 shadow-2xl transition-all duration-300 ease-out group-hover:shadow-[0_28px_60px_-10px] group-hover:shadow-foreground/40"
                    style={{ width: '320px', height: 'auto' }}
                  />
                </button>
              ));
            })()}
          </div>

          {/* Mobile — horizontal snap-scroll row */}
          <div className="md:hidden -mx-6 overflow-x-auto snap-x snap-mandatory scroll-smooth" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="flex gap-4 px-6 pb-4">
              {TEMPLATES.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => openLightbox(key, label)}
                  aria-label={`Preview ${label} invoice template`}
                  data-testid={`template-mobile-${key}`}
                  className="flex-shrink-0 snap-center focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded-md"
                  style={{ width: '78vw' }}
                >
                  <img
                    src={`/templates/${key}.png`}
                    alt={`${label} invoice template example`}
                    loading="lazy"
                    width="900"
                    height="1166"
                    className="w-full h-auto rounded-md border border-foreground/15 shadow-lg"
                  />
                  <div className="mt-3 text-center font-mono text-[11px] uppercase tracking-[0.28em] text-foreground/70">
                    {label}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Desktop names + link */}
          <div className="hidden md:flex flex-col items-center gap-4 mt-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-foreground/60">
              BRUTALIST &nbsp;·&nbsp; CLASSIC &nbsp;·&nbsp; MODERN &nbsp;·&nbsp; MINIMAL &nbsp;·&nbsp; AESTHETIC
            </div>
            <Link
              to="/register"
              className="font-body text-sm text-foreground underline underline-offset-4 hover:text-primary transition-colors"
              data-testid="template-showcase-cta"
            >
              Preview them with your own branding →
            </Link>
          </div>
          {/* Mobile CTA link */}
          <div className="md:hidden mt-6 text-center">
            <Link
              to="/register"
              className="font-body text-sm text-foreground underline underline-offset-4"
              data-testid="template-showcase-cta-mobile"
            >
              Preview them with your own branding →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 border-t border-foreground/15 bg-background" data-testid="landing-faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-accent mb-4 block">
              ? &nbsp; FAQ
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Questions, answered.
            </h2>
          </div>
          <div className="space-y-4">
            {LANDING_FAQS.map((faq, i) => (
              <div
                key={i}
                data-testid={`landing-faq-item-${i}`}
                className="bg-card border border-foreground/15 rounded-2xl p-6"
              >
                <h3 className="font-heading font-semibold text-lg mb-2">{faq.q}</h3>
                <p className="font-body text-foreground/70 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-foreground/90 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Template preview"
          data-testid="template-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            aria-label="Close preview"
            className="absolute top-4 right-4 text-background/80 hover:text-background p-2 rounded-full bg-background/10 hover:bg-background/20 transition-colors"
            data-testid="template-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxSrc}
            alt={lightboxAlt}
            className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Pricing — paper dominant, single Riso Blue recommended plan */}
      <section id="pricing" className="py-24 px-6 border-t border-foreground/15">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-accent mb-4 block">
              ¤ &nbsp; Pricing
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tight mb-4 leading-[0.95]">
              Simple. Fair. Yours.
            </h2>
            <p className="font-body text-foreground/70 max-w-md mx-auto">
              Start free and scale as your creative business grows.
            </p>
          </div>
          <div className="flex justify-center mb-12">
            <div
              className="inline-flex items-center rounded-full border border-foreground/15 bg-card p-1"
              data-testid="landing-billing-toggle"
            >
              <button
                type="button"
                onClick={() => setBillingInterval('month')}
                data-testid="landing-billing-toggle-monthly"
                className={`px-5 py-2 rounded-full font-mono text-xs uppercase tracking-[0.18em] transition-colors ${
                  billingInterval === 'month' ? 'bg-foreground text-background' : 'text-foreground/60 hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingInterval('year')}
                data-testid="landing-billing-toggle-annual"
                className={`px-5 py-2 rounded-full font-mono text-xs uppercase tracking-[0.18em] transition-colors ${
                  billingInterval === 'year' ? 'bg-foreground text-background' : 'text-foreground/60 hover:text-foreground'
                }`}
              >
                Annual
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
            {plans.map((plan, index) => {
              const isPopular = plan.popular;
              return (
                <div
                  key={index}
                  data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                  className={`relative rounded-2xl p-8 border flex flex-col ${
                    isPopular
                      ? 'bg-primary text-primary-foreground border-foreground/20'
                      : 'bg-card text-card-foreground border-foreground/15'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-warning text-warning-foreground font-mono text-[10px] uppercase tracking-[0.24em] px-3 py-1 rounded-full border border-foreground/40">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="mb-6">
                    <div className={`font-mono text-[11px] uppercase tracking-[0.28em] mb-3 ${isPopular ? 'opacity-80' : 'text-foreground/60'}`}>
                      Plan · {plan.name}
                    </div>
                    <h3 className="font-heading font-bold text-2xl tracking-tight">{plan.name}</h3>
                    <p className={`font-body text-sm mt-1 ${isPopular ? 'opacity-80' : 'text-foreground/60'}`}>
                      {plan.description}
                    </p>
                  </div>
                  <div className="mb-8 flex items-baseline gap-1">
                    <span className="font-heading font-bold text-6xl leading-none">{plan.price}</span>
                    <span className={`font-mono text-sm ${isPopular ? 'opacity-80' : 'text-foreground/60'}`}>{plan.period}</span>
                    {plan.savings ? (
                      <span
                        className={`ml-2 font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${
                          isPopular ? 'bg-warning text-warning-foreground border-foreground/40' : 'bg-warning/90 text-warning-foreground border-foreground/20'
                        }`}
                        data-testid={`pricing-savings-${plan.name.toLowerCase()}`}
                      >
                        Save {plan.savings}%
                      </span>
                    ) : null}
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isPopular ? 'text-primary-foreground' : 'text-primary'}`} />
                        <span className="font-body">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={plan.registerHref} className="w-full mt-auto">
                    <Button
                      variant={isPopular ? 'secondary' : 'outline'}
                      className={`w-full rounded-full ${isPopular ? '' : 'border-foreground/30'}`}
                      data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
          <p className="text-center font-body text-sm text-foreground/60 mt-10 max-w-2xl mx-auto">
            Online card payments: 3.4% + $0.30 per transaction, deducted from your payout. Clients pay the exact invoice amount.
          </p>
        </div>
      </section>

      {/* Final CTA — Riso Blue full-bleed */}
      <section className="py-24 px-6 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <span className="font-mono text-[11px] uppercase tracking-[0.32em] opacity-80 mb-6 block">
            ▲ &nbsp; Ready when you are
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tight mb-6 leading-[0.95]">
            Send your first invoice
            <br />
            in the next five minutes.
          </h2>
          <p className="font-body text-primary-foreground/85 mb-10 text-lg max-w-xl mx-auto">
            Spend less time on paperwork and more time on the work you love.
          </p>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="gap-2 rounded-full px-8 h-12 text-base" data-testid="footer-cta">
              Create your free account <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;

